import { type ReactNode, useContext, useMemo } from "react";
import { Controller } from "react-hook-form";
import { useLocalization } from "@fluent/react";
import { ContentCard, TextInput, Tooltip } from "@narmi/design_system";
import { modules, useFeature } from "byzantine";
import Account from "byzantine/src/Account";
import { FREQUENCIES } from "byzantine/src/Recurrence";
import { useRecipients } from "byzantine/src/dbbl/businessLogic/entities/recipients";
import { useIsBusinessUser } from "byzantine/src/dbbl/businessLogic/entities/users/hooks";
import { useCreateInternationalWireForm } from "byzantine/src/dbbl/businessLogic/modules/internationalWires";
import filters from "byzantine/src/filters";
import { catcat as cc } from "cerulean";

import CurrencyAmountInput from "../../../../../cerulean/src/ReactHookForm/CurrencyAmountInput";
import ComboAccountSelector from "../../../ComboAccountSelector";
import AccountContext from "../../../contexts/AccountContext";
import RecipientSelector from "../../../transfer/RecipientSelector";
import FrequencySelector from "../../FrequencySelector";
import styles from "./InternationalWire.module.scss";
import WireReasonSelector from "./WireReasonSelector";
import LimitsDialog from "../../LimitsDialog";
import { useLimits } from "../../../contexts/LimitsContext";
import getLimitsForDisplay from "../../limitsForDisplay";
import {
  handleInternationalWireFrequencyChange,
  InternationalWireRecurrenceEndCard,
  InternationalWireStartDateField,
} from "./InternationalWireRecurrenceEndCard";

type FreeFormUsdProps = {
  reasonsRes: API.InternationalWireReasonResponse;
  onAddRecipientClick: () => void;
  renderRecipientInstitutionDetails: () => ReactNode;
};

const FreeFormUsd = ({
  reasonsRes,
  onAddRecipientClick,
  renderRecipientInstitutionDetails,
}: FreeFormUsdProps) => {
  const { l10n } = useLocalization();
  const { featureEnabled } = useFeature();
  const { accounts } = useContext(AccountContext);
  const { control, setValue, watch } = useCreateInternationalWireForm();
  const isBusinessUser = useIsBusinessUser();
  const recipients = useRecipients();
  const limits = useLimits();
  const limitsForDisplay = useMemo(
    () =>
      getLimitsForDisplay(limits, [
        "international_wire",
        "organization_user_international_wire",
      ]),
    [limits]
  );
  const validAccounts = accounts.filter(
    (a: Account) => a.isInternal() && a.isValidTransferSource()
  );
  const {
    templateSelected,
    requiresAmount,
    requiresMemo,
    requiresWireReason,
    requiresNote,
  } = modules.internationalWires.useConditionalDetails();
  const showSchedulingControls =
    !isBusinessUser && featureEnabled("international_wires_consumer");
  const amount = watch("amount");
  const startDate = watch("startDate");
  const recurringRule = watch("recurringRule");

  const schedulingFields = showSchedulingControls ? (
    <>
      <div className="margin--bottom--l">
        <InternationalWireStartDateField
          control={control as any}
          label={l10n.getString("label-date-once")}
          setValue={setValue}
        />
      </div>
      <div
        className={
          recurringRule?.isVisible ? "margin--bottom--s" : "margin--bottom--l"
        }
      >
        <Controller
          name={"recurringRule.frequency" as any}
          control={control as any}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <FrequencySelector
              label={l10n.getString("label-frequency")}
              value={value || FREQUENCIES.ONCE}
              error={error?.message}
              onChange={(frequency: string) =>
                handleInternationalWireFrequencyChange(
                  frequency,
                  startDate,
                  onChange,
                  setValue
                )
              }
            />
          )}
        />
      </div>
      {recurringRule?.isVisible && (
        <div className={styles.recurrenceEndCard}>
          <InternationalWireRecurrenceEndCard
            amount={amount}
            count={recurringRule.count}
            control={control}
            ends={recurringRule.ends}
            frequency={recurringRule.frequency}
            startDate={startDate}
            setValue={setValue}
            untilDate={recurringRule.untilDate}
          />
        </div>
      )}
    </>
  ) : null;

  const renderWireReasons = () => {
    const reasons = Object.values(reasonsRes)?.[0];
    if (reasons?.length) {
      return (
        <div className={cc({ "margin--bottom--l": requiresNote })}>
          <Controller
            name={"wireReason"}
            control={control as any}
            render={({
              field: { onChange: controlOnChange, value },
              fieldState: { error },
            }) => {
              const onChange = (e: string) => {
                controlOnChange(e);
                setValue(
                  "wireReasonDescription",
                  filters.humanize(
                    reasons.find((reason) => reason.code === e)?.description ||
                      ""
                  )
                );
              };
              return (
                <WireReasonSelector
                  reasons={reasons}
                  onChange={onChange}
                  value={value}
                  label={l10n.getString("label-wire-reason")}
                  error={error?.message || ""}
                />
              );
            }}
          />
        </div>
      );
    }
    return null;
  };

  const amountField = (
    <div className="margin--bottom--l">
      <CurrencyAmountInput
        name="amount"
        control={control as any}
        label={l10n.getString("label-amount")}
        currency="USD"
      />
    </div>
  );

  const memoTooltip = (
    <Tooltip text={l10n.getString("tooltip-international-wire-memo-statement")}>
      <span className="narmi-icon-info" />
    </Tooltip>
  );

  const memoField = (
    <div className="margin--bottom--l">
      <Controller
        name={"memo"}
        control={control as any}
        render={({
          field: { onChange, onBlur, value, ref },
          fieldState: { error },
        }) => (
          <TextInput
            field="memo"
            onBlur={onBlur}
            ref={ref}
            label={l10n.getString("label-memo-optional")}
            endContent={memoTooltip}
            onChange={onChange}
            value={value ?? ""}
            error={error?.message}
          />
        )}
      />
    </div>
  );

  const noteField = (
    <Controller
      name={"note"}
      control={control as any}
      render={({
        field: { onChange, onBlur, value, ref },
        fieldState: { error },
      }) => (
        <TextInput
          field="note"
          onBlur={onBlur}
          ref={ref}
          label={l10n.getString("label-internal-note-optional")}
          onChange={onChange}
          value={value ?? ""}
          error={error?.message}
        />
      )}
    />
  );

  if (templateSelected) {
    const hasGapFields =
      requiresAmount || requiresMemo || requiresWireReason || requiresNote;

    return (
      <>
        {hasGapFields && (
          <ContentCard kind="bordered">
            <p className={styles.wireContainerSubHeader}>
              {l10n.getString("international-wire-form-subheader")}
            </p>
            {requiresAmount && amountField}
            {requiresMemo && memoField}
            {requiresWireReason && renderWireReasons()}
            {requiresNote && noteField}
            <LimitsDialog
              type={["international_wire"]}
              limitsForDisplay={limitsForDisplay}
            />
          </ContentCard>
        )}
        {!hasGapFields && (
          <LimitsDialog
            type={["international_wire"]}
            limitsForDisplay={limitsForDisplay}
          />
        )}
      </>
    );
  }

  return (
    <>
      <ContentCard kind="bordered">
        <p className={styles.wireContainerSubHeader}>
          {l10n.getString("international-wire-form-subheader")}
        </p>
        <div className="margin--bottom--l">
          <Controller
            name={"fromAccountId"}
            control={control as any}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <ComboAccountSelector
                value={value}
                isDestination={false}
                onChange={onChange}
                label={l10n.getString("label-from")}
                accounts={validAccounts}
                showAddExternalAccountLink={false}
                errorText={error?.message}
              />
            )}
          />
        </div>
        <div className="margin--bottom--l">
          <Controller
            name={"recipientId"}
            control={control as any}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <RecipientSelector
                onChange={onChange}
                value={value}
                label={l10n.getString("label-to")}
                recipients={recipients}
                onAddRecipientClick={onAddRecipientClick}
                error={error?.message}
                displayBottomAction
              />
            )}
          />
          {renderRecipientInstitutionDetails()}
        </div>
        {amountField}
        <LimitsDialog
          type={["international_wire"]}
          limitsForDisplay={limitsForDisplay}
        />
      </ContentCard>
      <ContentCard kind="bordered">
        <p className={styles.wireContainerSubHeader}>
          {l10n.getString("header-transfer-additional-details")}
        </p>
        {schedulingFields}
        {memoField}
        {renderWireReasons()}
        {noteField}
      </ContentCard>
    </>
  );
};

export default FreeFormUsd;
